// SPDX-License-Identifier: AGPL-3.0-or-later
import type { KeyBundle, Sealed } from "@almanac/crypto";
import type { FeedEntry, PushEntry } from "./events.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      // Required on every change; see AuthGuard on the server.
      ...(method === "GET" ? {} : { "X-Almanac": "1" }),
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 204) return undefined as T;
  const data: unknown = response.headers.get("content-type")?.includes("json") ? await response.json() : null;
  if (!response.ok) {
    const message = (data as { message?: unknown } | null)?.message;
    throw new ApiError(response.status, typeof message === "string" ? message : response.statusText, data);
  }
  return data as T;
}

export interface Me {
  id: string;
  email: string | null;
  displayName: string | null;
  role: "user" | "admin";
  locale: "en-CA" | "fr-CA";
  hasKeys: boolean;
}

export interface RemoteRecord {
  id: string;
  area: string;
  kind: string;
  ciphertext: Sealed | Record<string, never>;
  version: number;
  change: number;
  deleted: boolean;
}

export const api = {
  authConfig: () => call<{ mode: "oidc" | "dev" }>("GET", "/auth/config"),
  devLogin: (name: string) => call<void>("POST", "/auth/dev-login", { name }),
  logout: () => call<void>("POST", "/auth/logout"),
  me: () => call<Me>("GET", "/me"),
  updateMe: (changes: Partial<Pick<Me, "locale" | "displayName">>) => call<void>("PATCH", "/me", changes),
  deleteMe: () => call<void>("DELETE", "/me", { confirm: "DELETE" }),
  getKeys: () => call<{ bundle: KeyBundle; version: number }>("GET", "/keys"),
  putKeys: (bundle: KeyBundle, expectedVersion: number | null) => call<{ version: number }>("PUT", "/keys", { bundle, expectedVersion }),
  records: (since: number) => call<{ records: RemoteRecord[]; cursor: number; more: boolean }>("GET", `/records?since=${since}`),
  putRecord: (id: string, area: string, kind: string, ciphertext: Sealed, expectedVersion: number | null) =>
    call<RemoteRecord>("PUT", `/records/${id}`, { area, kind, ciphertext, expectedVersion }),
  deleteRecord: (id: string, expectedVersion: number) => call<RemoteRecord>("DELETE", `/records/${id}`, { expectedVersion }),
  startFeed: (name: string, entries: FeedEntry[]) => call<{ token: string }>("POST", "/calendar/feed", { name, entries }),
  updateFeed: (entries: FeedEntry[]) => call<void>("PUT", "/calendar/feed", { entries }),
  stopFeed: () => call<void>("DELETE", "/calendar/feed"),
  pushStatus: () => call<PushStatus>("GET", "/push"),
  savePush: (settings: PushSettings, entries: PushEntry[]) => call<void>("PUT", "/push", { ...settings, entries }),
  updatePushEntries: (entries: PushEntry[]) => call<void>("PUT", "/push/entries", { entries }),
  stopPush: () => call<void>("DELETE", "/push"),
  testPush: () => call<void>("POST", "/push/test"),
  pushAlert: (key: string, body: string) => call<void>("POST", "/push/alert", { key, body }),
};

/** The address calendar apps subscribe to. */
/** What the server keeps for phone notifications. The token is write-only. */
export interface PushSettings {
  serverUrl: string;
  topic: string;
  /** A new token, null to remove it, or left out to keep the saved one. */
  token?: string | null;
  timeZone: string;
  remindHour: number;
  leadDays: number;
  quietStart: number | null;
  quietEnd: number | null;
  digest: boolean;
}

export type PushStatus =
  | { enabled: false }
  | ({ enabled: true; hasToken: boolean; entries: number; lastError: string | null } & Omit<PushSettings, "token">);

export function feedUrl(token: string): string {
  return `${location.origin}/api/calendar/ics/${token}.ics`;
}
